import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getDeletedPatientTasks,
  getPatientTasks,
  restoreTask,
} from '../services/api';
import type {
  DeletedTaskResponse,
  TaskListResponse,
} from '../services/api/types.gen';
import { useStore } from '../store';
import { sortByLocalDate } from '../utils/dateUtils';
import { getBusinessErrorMessage } from '../utils/apiErrorDetail';
import { socConflictCopyFromError } from '../components/visit/socConflictCopy';
import { disciplinesForTitles } from '../utils/clinicalTitles';
import {
  filterTaskRows,
  taskTypeFilterForDisciplines,
  type TaskTypeFilterValue,
} from '../components/task/taskTypeFilters';
import { MAX_BATCH_SELECTED } from '../components/patient/patientDetailTaskConstants';

export type TaskView = 'active' | 'deleted' | 'all';

/** What the Documents tab needs to tell this episode's attachments apart. */
export interface DocumentsEpisodeScope {
  episodeId: string | null;
  taskEpisodeIds: Map<string, string>;
  taskDates: Map<string, string>;
  taskEpisodeIdsKnown: boolean;
  ready: boolean;
}

export interface UsePatientDetailTasksParams {
  patientId: string | undefined;
  selectedVisitId: string | null;
  effectiveSelectedEpisodeId: string | null;
  episodesSettledPatientId: string | null;
  /** Tracks the patient the page is currently on, for late-resolving fetches. */
  currentPatientIdRef: React.MutableRefObject<string | undefined>;
  /** Returns the page to the task list after a restore from a detail view. */
  closeTaskDetail: () => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
}

/**
 * Task loading, the deleted-task views, the table's type filter and the batch
 * selection for the patient chart's Tasks tab.
 *
 * Takes the selected episode rather than resolving it, because the episode
 * hook needs the tasks back (see usePatientDetailEpisodes).
 */
export function usePatientDetailTasks({
  patientId,
  selectedVisitId,
  effectiveSelectedEpisodeId,
  episodesSettledPatientId,
  currentPatientIdRef,
  closeTaskDetail,
  showSuccess,
  showError,
}: UsePatientDetailTasksParams) {
  const [tasks, setTasks] = useState<TaskListResponse[]>([]);
  // Soft-deleted tasks, kept in their own list so nothing that reads `tasks`
  // (the calendar, the tab count, batch actions, billing selection) has to
  // learn to skip them. The table swaps which list it renders.
  // Which slice of the episode's tasks the table shows. "all" interleaves live
  // and deleted rows, so deletedness becomes a per-row property rather than a
  // property of the view.
  const [taskView, setTaskView] = useState<TaskView>('active');
  // Both non-default views need the deleted rows fetched.
  const showDeletedTasks = taskView !== 'active';
  const [deletedTasks, setDeletedTasks] = useState<DeletedTaskResponse[]>([]);
  const [deletedTasksLoading, setDeletedTasksLoading] = useState(false);
  // Which episode the rows in `deletedTasks` belong to. The empty state is
  // gated on this matching the episode on screen, so the frame between flipping
  // the toggle and the fetch starting says "loading" rather than asserting
  // nothing was ever deleted, and an episode switch does not paint the previous
  // episode's deletions for a frame.
  const [deletedTasksEpisodeId, setDeletedTasksEpisodeId] = useState<
    string | null
  >(null);
  // One deleted task resolved by id, for a detail URL opened directly. Kept
  // apart from `deletedTasks` so the table keeps showing exactly one episode's
  // deletions even though this lookup is not episode-scoped.
  const [linkedDeletedTask, setLinkedDeletedTask] =
    useState<DeletedTaskResponse | null>(null);
  const linkedLookupTaskIdRef = useRef<string | null>(null);
  const [deletedTasksError, setDeletedTasksError] = useState<string | null>(
    null
  );
  // The task currently being restored, so its own control shows the wait and
  // nothing else on the page is disabled by one row's request.
  const [restoringTaskId, setRestoringTaskId] = useState<string | null>(null);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [visitsError, setVisitsError] = useState<string | null>(null);
  const [tasksPatientId, setTasksPatientId] = useState<string | null>(null);
  // Which patient's task fetch has *settled*, successfully or not. Distinct from
  // `tasksPatientId`, which only advances on success: a consumer that must not
  // paint until the tasks are in needs to know when to stop waiting, and a
  // failed or empty-bodied fetch never sets `tasksPatientId`.
  const [tasksSettledPatientId, setTasksSettledPatientId] = useState<
    string | null
  >(null);
  const [tasksRefreshVersion, setTasksRefreshVersion] = useState(0);
  const taskRequestIdRef = useRef(0);

  // Batch-actions selection for the episode task table (ROG-2506). Capped at
  // MAX_BATCH_SELECTED; cleared whenever the active episode changes (the table
  // does not remount on episode switch — see the effect below).
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    () => new Set()
  );

  // The tab this clinician's own work lives on. `/auth/me` may not have
  // resolved on first render, so this starts as the default and the effect
  // below applies it once titles arrive (EMR-1431).
  const currentUserTitles = useStore(state => state.user?.user_types);
  const disciplineTaskTypeFilter = useMemo(
    () => taskTypeFilterForDisciplines(disciplinesForTitles(currentUserTitles)),
    [currentUserTitles]
  );
  const [tableTaskTypeFilter, setTableTaskTypeFilter] =
    useState<TaskTypeFilterValue>(disciplineTaskTypeFilter);
  // Set once the operator picks a tab themselves, so a late-arriving
  // `/auth/me` does not yank them back to their own discipline.
  const taskTypeFilterTouchedRef = useRef(false);
  const setTableTaskTypeFilterFromUser = useCallback(
    (filter: TaskTypeFilterValue) => {
      taskTypeFilterTouchedRef.current = true;
      setTableTaskTypeFilter(filter);
    },
    []
  );
  const previousPatientIdRef = useRef(patientId);

  const fetchTasks = useCallback(
    async (options?: { silent?: boolean }) => {
      // A silent refresh reloads task data without flipping the section into its
      // loading state, so editing a single task (date/clinician) updates the row
      // in place instead of flashing the whole visits section away and back.
      const silent = options?.silent ?? false;
      if (!patientId) return;
      const requestedPatientId = patientId;
      const requestId = taskRequestIdRef.current + 1;
      taskRequestIdRef.current = requestId;
      const isLatestTaskRequest = () =>
        currentPatientIdRef.current === requestedPatientId &&
        taskRequestIdRef.current === requestId;

      try {
        if (!silent) {
          setVisitsLoading(true);
          setVisitsError(null);
        }
        const tasksData = await getPatientTasks({
          path: { patient_id: requestedPatientId },
        });
        if (!isLatestTaskRequest()) {
          return;
        }
        if (tasksData?.data) {
          setTasks(tasksData.data);
          setTasksPatientId(requestedPatientId);
          setTasksRefreshVersion(version => version + 1);
          return true;
        }
        return false;
      } catch (err) {
        if (!isLatestTaskRequest()) {
          return;
        }
        console.error('Error fetching visits/tasks:', err);
        if (!silent) {
          setVisitsError('Failed to load tasks');
        }
        return false;
      } finally {
        // Always clear the loading flag for the latest request, even on a
        // silent fetch: a silent refresh can supersede a non-silent one that
        // set visitsLoading, and it must take over clearing it. Clearing when
        // already false is an idempotent no-op.
        if (isLatestTaskRequest()) {
          setVisitsLoading(false);
          // Every exit from the try runs this, including the
          // resolved-but-empty-bodied path that returns false without setting
          // `tasksPatientId` or `visitsError`, so a consumer waiting on the
          // tasks cannot wait forever.
          setTasksSettledPatientId(requestedPatientId);
        }
      }
    },
    [currentPatientIdRef, patientId]
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    setTasksPatientId(null);
    setTasksSettledPatientId(null);
    setTasksRefreshVersion(0);
  }, [patientId]);

  // Leaving the patient drops the deleted view. Reopening the page on the live
  // list is the safe default: the deleted list is a deliberate detour, not a
  // mode to be returned to by surprise. Declared before the fetch below so a
  // patient switch clears the view before the fetch effect can fire once with
  // the new patient and the old patient's episode.
  useEffect(() => {
    setTaskView('active');
    setDeletedTasks([]);
    setDeletedTasksEpisodeId(null);
    setDeletedTasksError(null);
    setLinkedDeletedTask(null);
  }, [patientId]);

  // Deleted rows for the episode on screen. Scoped server-side by episode_id
  // rather than fetched whole and filtered here, so a patient with years of
  // deletions still costs one episode's worth.
  useEffect(() => {
    if (!showDeletedTasks || !patientId || !effectiveSelectedEpisodeId) {
      return;
    }
    const requestedPatientId = patientId;
    const episodeId = effectiveSelectedEpisodeId;
    let cancelled = false;
    setDeletedTasksLoading(true);
    setDeletedTasksError(null);
    // throwOnError so an HTTP failure reaches the catch. The client's default
    // resolves with `data` undefined, which would render "no deleted tasks" for
    // a server error, the worst possible answer on a view whose job is to prove
    // a record still exists.
    getDeletedPatientTasks({
      path: { patient_id: requestedPatientId },
      query: { episode_id: episodeId },
      throwOnError: true,
    })
      .then(response => {
        if (cancelled) return;
        setDeletedTasks(response.data ?? []);
        setDeletedTasksEpisodeId(episodeId);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Failed to load deleted tasks', err);
        setDeletedTasks([]);
        // Stamped on failure too, so the error branch is reachable: without it
        // the not-ready check below would hold the view on "loading" forever.
        setDeletedTasksEpisodeId(episodeId);
        setDeletedTasksError('Unable to load deleted tasks. Please try again.');
      })
      .finally(() => {
        // Cleared even when superseded. The flag is shared with whatever fetch
        // replaces this one, and leaving it set on a toggle-off strands the
        // table on "Loading deleted tasks..." with nothing in flight.
        setDeletedTasksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    showDeletedTasks,
    patientId,
    effectiveSelectedEpisodeId,
    tasksRefreshVersion,
  ]);

  // A detail URL opened directly (a reload, a bookmark, a shared link) names a
  // task the live list does not have. Resolving just that one row is what lets
  // the URL open the deleted chart instead of silently dropping back to the
  // task list. Gated on the live list having actually loaded, so an id is only
  // judged missing once there is something to miss.
  //
  // Unscoped, unlike the table's fetch: the URL carries a task id and no
  // episode, and the task is often in an older episode than the one the page
  // defaults to, so scoping this to the selected episode would answer "not
  // found" for exactly the links that need it. Kept in its own state so the
  // table keeps showing one episode's deletions and nothing else.
  useEffect(() => {
    if (!patientId || !selectedVisitId) return;
    if (tasksPatientId !== patientId) return;
    if (tasks.some(task => task.id === selectedVisitId)) return;
    if (deletedTasks.some(task => task.id === selectedVisitId)) return;
    if (linkedDeletedTask?.id === selectedVisitId) return;
    // One attempt per id. A lookup that comes back empty leaves the state at
    // null, which no dependency here can distinguish from "not tried yet", so
    // without this every later task refresh would re-ask the same question.
    if (linkedLookupTaskIdRef.current === selectedVisitId) return;
    linkedLookupTaskIdRef.current = selectedVisitId;
    const requestedTaskId = selectedVisitId;
    let cancelled = false;
    getDeletedPatientTasks({
      path: { patient_id: patientId },
      throwOnError: true,
    })
      .then(response => {
        if (cancelled) return;
        setLinkedDeletedTask(
          response.data?.find(task => task.id === requestedTaskId) ?? null
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // No user-facing error: the page still renders the task list, which is
        // what it showed before this lookup existed.
        console.error('Failed to resolve deleted task from URL', err);
        setLinkedDeletedTask(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    patientId,
    selectedVisitId,
    tasksPatientId,
    tasks,
    deletedTasks,
    linkedDeletedTask,
  ]);

  // The episode table does not remount when the active episode changes, so
  // batch selection must be cleared explicitly here — otherwise a selection
  // made under one episode would silently leak into another.
  useEffect(() => {
    setSelectedTaskIds(new Set());
  }, [effectiveSelectedEpisodeId]);

  useEffect(() => {
    if (previousPatientIdRef.current === patientId) {
      return;
    }
    previousPatientIdRef.current = patientId;
    taskTypeFilterTouchedRef.current = false;
    setTableTaskTypeFilter(disciplineTaskTypeFilter);
  }, [disciplineTaskTypeFilter, patientId]);

  // Seed the tab from the signed-in clinician's titles once `/auth/me` lands.
  // The dep is the resolved tab (a string), not the titles array, so the
  // two-minute `/auth/me` refresh does not re-fire this on every poll.
  useEffect(() => {
    if (taskTypeFilterTouchedRef.current) {
      return;
    }
    setTableTaskTypeFilter(disciplineTaskTypeFilter);
  }, [disciplineTaskTypeFilter]);

  // Undelete a task and put it back in the live list. One endpoint for all four
  // subtypes, unlike deletion: the restore rules differ per type but the
  // request does not, so there is nothing here to branch on.
  const handleRestoreTask = useCallback(
    async (task: TaskListResponse, options?: { closeDetail?: boolean }) => {
      setRestoringTaskId(task.id);
      try {
        await restoreTask({ path: { task_id: task.id }, throwOnError: true });
        // Restored from the detail view, the panel is now showing a live task in
        // its read-only deleted mode. Returning to the list is both truthful and
        // where the restored row now is.
        if (options?.closeDetail) {
          closeTaskDetail();
        }
        // Drop the by-id lookup that resolved this task while it was deleted, so
        // a detail URL reopened straight afterwards does not reuse a stale row.
        setLinkedDeletedTask(prev => (prev?.id === task.id ? null : prev));
        linkedLookupTaskIdRef.current = null;
        // Refreshing the live list also bumps tasksRefreshVersion, which is what
        // re-fetches the deleted list this row is leaving.
        await fetchTasks();
        showSuccess('Task Restored', 'The task is back in the task list.');
      } catch (err) {
        console.error('Error restoring task:', err);
        showError(
          'Restore Failed',
          socConflictCopyFromError(err) ??
            getBusinessErrorMessage(err) ??
            'Unable to restore this task. Please try again.'
        );
      } finally {
        setRestoringTaskId(null);
      }
    },
    [closeTaskDetail, fetchTasks, showError, showSuccess]
  );

  const allTasks = useMemo(
    () => sortByLocalDate(tasks, 'task_date', 'asc'),
    [tasks]
  );
  // Every visit on the patient, across all of their episodes. Its one consumer
  // is the wound-assessment provenance lookup, which has to name visits outside
  // the episode on screen. Visit pickers read `episodeVisitTasks` instead.
  const visitTasks = useMemo(
    () => allTasks.filter(task => task.task_type === 'visit'),
    [allTasks]
  );

  // `effectiveSelectedEpisodeId` stands in for the selected episode object
  // here: it is null exactly when there is no selected episode.
  const tasksForEpisode = useMemo(
    () =>
      effectiveSelectedEpisodeId
        ? sortByLocalDate(
            tasks.filter(t => t.episode_id === effectiveSelectedEpisodeId),
            'task_date',
            'asc'
          )
        : [],
    [tasks, effectiveSelectedEpisodeId]
  );

  // The visits the episode on screen actually contains. Every visit picker on
  // this page offers these and nothing else: the header names one episode, so
  // offering a neighbouring episode's visit as context for a MAR, a wound
  // assessment or a goal only invites recording it against the wrong episode.
  const episodeVisitTasks = useMemo(
    () => tasksForEpisode.filter(task => task.task_type === 'visit'),
    [tasksForEpisode]
  );

  const filteredTasksForEpisode = useMemo(
    () => filterTaskRows(tasksForEpisode, tableTaskTypeFilter),
    [tasksForEpisode, tableTaskTypeFilter]
  );

  const filteredDeletedTasks = useMemo(
    () => filterTaskRows(deletedTasks, tableTaskTypeFilter),
    [deletedTasks, tableTaskTypeFilter]
  );

  // True once `deletedTasks` actually describes the episode on screen. Until
  // then the list is either empty or the previous episode's, and neither is a
  // truthful answer to "what was deleted here".
  //
  // With no episode there is nothing to fetch, so the answer is ready and it is
  // "nothing". Without this arm a patient who has no episode yet, or whose
  // episode load failed, strands the view on a spinner with no request behind
  // it, and the empty task list is exactly when someone goes looking here.
  const deletedTasksReady =
    effectiveSelectedEpisodeId === null ||
    (deletedTasksEpisodeId !== null &&
      deletedTasksEpisodeId === effectiveSelectedEpisodeId);

  // The rows the table paints. A deleted row has the same shape as a live one,
  // so the renderer is shared and only the affordances differ.
  const displayedTasks: TaskListResponse[] = useMemo(() => {
    if (taskView === 'active') return filteredTasksForEpisode;
    // Until the deleted rows describe the episode on screen, "deleted" has
    // nothing truthful to show, while "all" can still show the live half.
    if (!deletedTasksReady) {
      return taskView === 'all' ? filteredTasksForEpisode : [];
    }
    if (taskView === 'deleted') return filteredDeletedTasks;
    // Interleaved by the date the row displays, so a deleted task sits where it
    // would have sat when it was live. Only "all" is sorted here: the other two
    // views keep the order their source already arrived in.
    const sortKey = (task: TaskListResponse) =>
      task.scheduled_date || task.task_date || '';
    return [...filteredTasksForEpisode, ...filteredDeletedTasks].sort(
      (a, b) => {
        const byDate = sortKey(a).localeCompare(sortKey(b));
        return byDate !== 0 ? byDate : a.id.localeCompare(b.id);
      }
    );
  }, [
    taskView,
    deletedTasksReady,
    filteredTasksForEpisode,
    filteredDeletedTasks,
  ]);

  // Deletion attribution for the row currently being rendered, by task id.
  const deletedTaskById = useMemo(
    () => new Map(deletedTasks.map(task => [task.id, task])),
    [deletedTasks]
  );

  // The episode's tasks that can carry an attachment. Two consumers: the
  // Documents tab's scope dropdown, and `calendarTasks` below, so a change here
  // moves the visits calendar too.
  //
  // Gated on the tasks describing this patient for the same reason the map below
  // is: `tasks` survives patient navigation, so on the first render after the
  // patient changes an ungated list would offer the previous patient's visits.
  const documentContextTasks = useMemo(
    () =>
      tasksPatientId === patientId
        ? tasksForEpisode.filter(t => t.task_type !== 'patient_communication')
        : [],
    [tasksForEpisode, tasksPatientId, patientId]
  );

  // Which episode each of the patient's tasks belongs to, so the Documents tab
  // can tell an attachment on this episode from one on an earlier episode.
  // `tasks` is not cleared on patient navigation while `tasksPatientId` is, so
  // gating on it is what keeps the previous patient's map from being applied to
  // this one; the empty map then reads as "cannot say" and nothing is hidden.
  // Built from every task, communications included, so an attachment on a
  // communication in this episode is not mistaken for another episode's.
  const taskEpisodeIds = useMemo(
    () =>
      tasksPatientId === patientId
        ? new Map(tasks.map(task => [task.id, task.episode_id]))
        : new Map<string, string>(),
    [tasks, tasksPatientId, patientId]
  );

  // The date each task displays, patient-wide. A visit or order shows its service
  // date rather than its scheduled date, and the two diverge once a visit is
  // charted on a different day, so a document has to be dated from here to agree
  // with the task it hangs off wherever both are on screen. Gated on the task
  // fetch alone, like the episode map above; the episode fetch has no bearing on
  // what a task is dated.
  const taskDates = useMemo(
    () =>
      tasksPatientId === patientId
        ? new Map(
            tasks.map(task => [task.id, task.task_date ?? task.scheduled_date])
          )
        : new Map<string, string>(),
    [tasks, tasksPatientId, patientId]
  );

  // Both fetches gate this, not just the tasks one. The episode decides which
  // documents the tab shows, so handing over the previous patient's episode (or
  // "none yet" while the episodes are in flight) would filter this patient's
  // chart against the wrong episode, or paint the whole chart and then narrow it
  // once the episode arrives. `episodes` is cleared by the patient-change effect,
  // but that effect has not run on the first render after the patient changes,
  // which is why the episode is read through the settled marker and not straight
  // off the selected episode. That last part is defensive: on the same render the
  // task marker is stale too, so the episode scope is unavailable anyway.
  const documentsEpisodeScope = useMemo<DocumentsEpisodeScope>(
    () => ({
      episodeId:
        episodesSettledPatientId === patientId
          ? effectiveSelectedEpisodeId
          : null,
      taskEpisodeIds,
      taskDates,
      // The maps describe this patient only once the fetch succeeded. A failed
      // fetch settles with an empty map, and the tab must not report that as a
      // filtered episode view.
      taskEpisodeIdsKnown: tasksPatientId === patientId,
      ready:
        tasksSettledPatientId === patientId &&
        episodesSettledPatientId === patientId,
    }),
    [
      effectiveSelectedEpisodeId,
      taskEpisodeIds,
      taskDates,
      tasksPatientId,
      tasksSettledPatientId,
      episodesSettledPatientId,
      patientId,
    ]
  );

  const calendarTasks = useMemo(
    () => filterTaskRows(documentContextTasks, tableTaskTypeFilter),
    [documentContextTasks, tableTaskTypeFilter]
  );

  // ===== Batch actions for the episode task table (ROG-2506) =====
  // Resolve from the full `tasks` list (not just the filtered/visible rows) so a
  // cross-discipline selection survives tab switches and shows in the manifest.
  const selectedTasks = useMemo(
    () => tasks.filter(t => selectedTaskIds.has(t.id)),
    [tasks, selectedTaskIds]
  );
  const atSelectionCap = selectedTaskIds.size >= MAX_BATCH_SELECTED;

  const toggleTaskSelected = useCallback((taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else if (next.size < MAX_BATCH_SELECTED) {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const removeTaskFromSelection = useCallback((task: TaskListResponse) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      next.delete(task.id);
      return next;
    });
  }, []);

  const clearTaskSelection = useCallback(
    () => setSelectedTaskIds(new Set()),
    []
  );

  // Header checkbox: checked when every visible row is selected, indeterminate
  // when only some are, unchecked otherwise.
  const selectAllState = useMemo<boolean | 'indeterminate'>(() => {
    if (filteredTasksForEpisode.length === 0) return false;
    const selectedInView = filteredTasksForEpisode.filter(t =>
      selectedTaskIds.has(t.id)
    ).length;
    if (selectedInView === 0) return false;
    if (selectedInView === filteredTasksForEpisode.length) return true;
    return 'indeterminate';
  }, [filteredTasksForEpisode, selectedTaskIds]);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedTaskIds(prev => {
      // Toggle on the checked/indeterminate state (anything selected → clear),
      // so the header still clears when the 30-cap prevented selecting them all.
      const anyInViewSelected = filteredTasksForEpisode.some(t =>
        prev.has(t.id)
      );
      const next = new Set(prev);
      if (anyInViewSelected) {
        filteredTasksForEpisode.forEach(t => next.delete(t.id));
      } else {
        // Select up to the cap, preserving any already-selected out-of-view rows.
        for (const t of filteredTasksForEpisode) {
          if (next.size >= MAX_BATCH_SELECTED) break;
          next.add(t.id);
        }
      }
      return next;
    });
  }, [filteredTasksForEpisode]);

  return {
    // Raw task state
    tasks,
    setTasks,
    allTasks,
    visitTasks,
    visitsLoading,
    visitsError,
    tasksPatientId,
    tasksSettledPatientId,
    tasksRefreshVersion,
    fetchTasks,

    // Deleted views
    taskView,
    setTaskView,
    showDeletedTasks,
    deletedTasks,
    deletedTasksLoading,
    deletedTasksError,
    deletedTasksReady,
    deletedTaskById,
    linkedDeletedTask,
    restoringTaskId,
    handleRestoreTask,

    // Filters and derived rows
    tableTaskTypeFilter,
    setTableTaskTypeFilterFromUser,
    tasksForEpisode,
    episodeVisitTasks,
    filteredTasksForEpisode,
    displayedTasks,
    calendarTasks,
    documentContextTasks,
    documentsEpisodeScope,

    // Batch selection
    selectedTaskIds,
    setSelectedTaskIds,
    selectedTasks,
    atSelectionCap,
    selectAllState,
    toggleTaskSelected,
    removeTaskFromSelection,
    clearTaskSelection,
    handleToggleSelectAll,
  };
}
